import { InformationCircleIcon, MagnifyingGlassIcon, PlusIcon, UsersIcon } from "@heroicons/react/24/outline";
import { useEffect, useState, useCallback } from "react"
import { format, isValid } from "date-fns";
import { SelectDropdown } from "../../../components/SelectDropdown";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "../../../helpers/useDebounce";
import { useDispatch, useSelector } from "react-redux";
import { ROUTES } from "../../../consts/general";
import { ACTIVE, ADMIN, enumUserStatuses } from "../../../consts/management/users/users";
import { UsersManagementLoadingKeys, addNewUser, disableUser, enableUser, getRoles, getRolesToReassign, getUserRolesBySelect, getUsers, makeDuplicateRole, removeRole, removeUser, updateRoleInfo, updateUserInfo } from "../../../slices/managementSlices/usersSettingsSlice";
import { OverlayGrayBackground } from "../../../components/OverlayGrayBackground";
import { UserActionsDropdown } from "../../../components/users/UserActionsDropdown";
import { UsersTab } from "../../../components/users/UsersTab";
import { DisableAccessModal } from "../../../components/users/DisableAccessModal";
import { EnableAccessModal } from "../../../components/users/EnableAccessModal";
import { RemoveUserModal } from "../../../components/users/RemoveUserModal";
import { UserDrawer } from "../../../components/users/UserDrawer";
import { AddUserModal } from "../../../components/users/AddUserModal";
import { RolesActionsDropdown } from "../../../components/users/RolesActionsDropdown";
import { RemoveRoleModal } from "../../../components/users/RemoveRoleModal";
import { RoleDrawer } from "../../../components/users/RoleDrawer";
import { LoaderSpinner } from "../../../components/LoaderSpinner";
import { toast } from "react-toastify";
import { ConfrimModal } from "../../../components/ConfrimModal";
import { AuditDrawer } from "../../../components/users/AuditDrawer";
import { getPermissionCatalog } from "../../../components/Sidebar/const";

const defaultSidebarStatuses = {
    user: {
        isOpen: false
    },
    role: {
        isOpen: false
    },
    roleCreate: {
        isOpen: false
    },
    audit: {
        isOpen: false
    }
};

const totalPermissionTabs = getPermissionCatalog().length;

const getUserStatusColor = (status) => {
    switch(status) {
        case (ACTIVE): 
            return "bg-success-light text-green-800 border border-green-200";
        default: 
            return "bg-gray-100 text-gray-700 border border-gray-200";
    }
}

const getUserRoleColor = (role) => {
    switch(role) {
        case (ADMIN): 
            return "bg-primary text-white border border-primary";
        default: 
            return "bg-gray-100 text-gray-700 border border-gray-200";
    }
}

const getUserInitials = (name) => {
    const initials = String(name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();
    return initials || "?";
};

const formatDisplayName = (value) => String(value || "")
    .toLowerCase()
    .replace(/(^|[\s_-])([a-z])/g, (match) => match.toUpperCase());

const formatRoleTimestamp = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (!isValid(date)) return null;
    return {
        date: format(date, "MMMM dd, yyyy"),
        time: format(date, "MMM d 'at' h:mm a"),
    };
};

export const UsersSettings = ({
    access
}) => {
    const pageParams = window?.location?.search ? new URLSearchParams(window?.location?.search) : null;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [tab, setTab] = useState(pageParams?.get('tab') || "users");
    const [currentRole, setCurrentRole] = useState(pageParams?.get('role') || "all_roles");
    const [currentStatus, setCurrentStatus] = useState(pageParams?.get('status') || "all_status");
    const [search, setSearch] = useState(pageParams?.get('search') || "");
    const [openedDropdownUserId, setOpenedDropdownUserId] = useState(null);
    const currentSearchValue = useDebounce(search, 300);
    const [sidebar, setSidebar] = useState(defaultSidebarStatuses);
    const [openedModals, setOpenedModals] = useState({
        create: {
            isOpen: false
        }, 
        disable: {
            isOpen: false
        },
        enable: {
            isOpen: false
        },
        remove: {
            isOpen: false
        },
        removeRole: {
            isOpen: false
        },
        duplicateRole: {
            isOpen: false
        }
    })

    const {
        users,
        roles,
        rolesToReassign,
        userRoles,
        isFirstLoadComplete,
        loadingKeys: usersLoadingKeys,
        loading: usersLoading
    } = useSelector((state) => state.usersSettings);

    useEffect(() => {
        dispatch(getUserRolesBySelect({}));
        dispatch(getRolesToReassign({}));
    }, []);

    useEffect(() => {
        setSidebar(defaultSidebarStatuses);
    }, [tab]);

    const updateRoute = useCallback(() => {
        if (!tab) return;
        let params = { tab, search: currentSearchValue };
        if (tab === "users") {
            params = {
                ...params,
                role: currentRole,
                status: currentStatus
            };
        } 
        let url = `${ROUTES.USERS_SETTINGS}?${new URLSearchParams(params)}`;
        navigate(url);
        if (tab === "users") {
            dispatch(getUsers({
                search: currentSearchValue,
                role: currentRole,
                status: currentStatus
            }));
        } 
        if (tab === "roles") {
            setCurrentRole("all_roles");
            setCurrentStatus("all_status");
            dispatch(getRoles({
                search: currentSearchValue,
            }));
        }
    }, [currentSearchValue, currentRole, currentStatus, tab, navigate, dispatch]);

    useEffect(() => {
        updateRoute();
    }, [updateRoute]);

    const handleAuditClick = () => {
        setSidebar((prev) => {
            return {
                ...prev,
                audit: {
                    isOpen: true,
                }
            }
        });
    };

    // Users
    const handleRemoveClick = (user) => {
        if (access === "edit") {
            setOpenedModals((prev) => {
                return {
                    ...prev,
                    remove: {
                        isOpen: true,
                        user
                    }
                }
            });
        }
    };
    const handleRemoveSubmitClick = (id) => {
        if (access === "edit") {
            dispatch(removeUser({
                id, 
                search: currentSearchValue,
                role: currentRole,
                status: currentStatus
            })).unwrap();
            setOpenedModals((prev) => {
                return {
                    ...prev,
                    remove: {
                        isOpen: false
                    }
                }
            });
        }
    };
    const handleDisableClick = (user) => {
        if (access === "edit") {
            setOpenedModals((prev) => {
                return {
                    ...prev,
                    disable: {
                        isOpen: true,
                        user
                    }
                }
            });
        }
    };
    const handleDisableSubmitClick = (id, reason) => {
        if (access === "edit") {
            dispatch(disableUser({
                id, 
                reason,
                search: currentSearchValue,
                role: currentRole,
                status: currentStatus
            })).unwrap();
            setOpenedModals((prev) => {
                return {
                    ...prev,
                    disable: {
                        isOpen: false
                    }
                }
            });
        }
    };
    const handleEnableClick = (user) => {
        if (access === "edit") {
            setOpenedModals((prev) => {
                return {
                    ...prev,
                    enable: {
                        isOpen: true,
                        user
                    }
                }
            });
        }
    };
    const handleEnableSubmitClick = (id) => {
        if (access === "edit") {
            dispatch(enableUser({
                id, 
                search: currentSearchValue,
                role: currentRole,
                status: currentStatus
            })).unwrap();
            setOpenedModals((prev) => {
                return {
                    ...prev,
                    enable: {
                        isOpen: false
                    }
                }
            });
        }
    };
    const handleEditClick = (user) => {
        if (access === "edit") {
            setSidebar((prev) => {
                return {
                    ...prev,
                    user: {
                        isOpen: true,
                        user
                    }
                }
            });
        }
    };
    const handleUpdateUser = async ({
        id,
        newRoleId,
        fullName,
        email,
        password,
        slackId
    }) => {
        if (access === "edit") {
            await dispatch(updateUserInfo({
                id,
                newRoleId,
                fullName,
                email,
                password,
                slackId,
                search: currentSearchValue,
                role: currentRole,
                status: currentStatus
            })).unwrap();
            setSidebar({
                user: {
                    isOpen: false,
                },
                role: {
                    isOpen: false
                }
            });
        }
    };
    const handleAddUser = async ({
        fullName, 
        email, 
        password, 
        selectedRole,
        slackId
    }) => {
        if (access === "edit") {
            const res = await dispatch(addNewUser({
                fullName, 
                email, 
                password, 
                selectedRole,
                slackId,
                search: currentSearchValue,
                role: currentRole,
                status: currentStatus
            })).unwrap();
            if (res?.message) {
                toast.error(res?.message, { position: "bottom-right" })
                return;
            };
            setOpenedModals((prev) => {
                return {
                    ...prev,
                    create: {
                        isOpen: false
                    }
                }
            });
        }
    };
    const handleAddUserClick = () => {
        if (access === "edit") {
            setOpenedModals((prev) => {
                return {
                    ...prev,
                    create: {
                        isOpen: true,
                    }
                }
            });
        }
    };
    // Roles
    const handleAddRoleClick = () => {
        if (access === "edit") {
            setSidebar((prev) => {
                return {
                    ...prev,
                    roleCreate: {
                        isOpen: true,
                    }
                }
            });
        }
    }
    const handleRemoveRoleClick = (role) => {
        if (access === "edit") {
            setOpenedModals((prev) => {
                return {
                    ...prev,
                    removeRole: {
                        isOpen: true,
                        role
                    }
                }
            });
        }
    };
    const removeRoleSubmitClick = async (id, newRole) => {
        if (access === "edit") {
            dispatch(removeRole({
                id, 
                newRole,
                search: currentSearchValue,
            })).unwrap();
            await dispatch(getUserRolesBySelect({})).unwrap();
            await dispatch(getRolesToReassign({})).unwrap();
            setOpenedModals((prev) => {
                return {
                    ...prev,
                    removeRole: {
                        isOpen: false
                    }
                }
            });
        }
    }
    const handleDuplicateRoleClick = (role) => {
        if (access === "edit") {
            setOpenedModals((prev) => {
                return {
                    ...prev,
                    duplicateRole: {
                        isOpen: true,
                        role
                    }
                }
            });
        }
    };
    const handleDuplicateSubmitClick = async () => {
        if (access === "edit") {
            const res = await dispatch(makeDuplicateRole({ 
                id: openedModals?.duplicateRole?.role?.id,
                search: currentSearchValue,
            })).unwrap();
            if (res?.message) {
                toast.error(res?.message, { position: "bottom-right" })
                return;
            };
            setOpenedModals((prev) => {
                return {
                    ...prev,
                    duplicateRole: {
                        isOpen: false
                    }
                }
            });
        }
    }
    const handleEditRoleClick = (role) => {
        if (access === "edit") {
            setSidebar((prev) => {
                return {
                    ...prev,
                    role: {
                        isOpen: true,
                        role
                    }
                }
            });
        }
    };
    const handleUpdateRole = async ({
        roleId,
        roleName,
        description,
        department,
        companyWideGovernance,
        permissions
    }) => {
        if (access === "edit") {
            const res = await dispatch(updateRoleInfo({
                roleId,
                roleName,
                description,
                department,
                companyWideGovernance,
                permissions,
                search: currentSearchValue,
            })).unwrap();
            if (res?.message) {
                toast.error(res?.message, { position: "bottom-right" })
                return;
            };
            setSidebar({
                user: {
                    isOpen: false,
                },
                role: {
                    isOpen: false
                },
                roleCreate: {
                    isOpen: false
                }
            });
            await dispatch(getUserRolesBySelect({})).unwrap();
            await dispatch(getRolesToReassign({})).unwrap();
        }
    }

    const countPermissions = (permissions) => {
        let editCount = 0;
        let viewCount = 0;

        (permissions || []).forEach((permissionGroup) => {
            const [_, children] = Object.entries(permissionGroup || {})[0] || [];
                (children || []).forEach((child) => {
                const [__, access] = Object.entries(child || {})[0] || [];
                if (access === "edit") editCount++;
                if (access === "view") viewCount++;
            });
        });

        return { editCount, viewCount, total: editCount + viewCount };
    };

    const usersIsLoading = () => usersLoading && usersLoadingKeys.includes(UsersManagementLoadingKeys.getUsers);
    const userRolesIsLoading = () => usersLoading && usersLoadingKeys.includes(UsersManagementLoadingKeys.getUserRolesBySelect);
    const userUpdateIsLoading = () => usersLoading && usersLoadingKeys.includes(UsersManagementLoadingKeys.updateUserInfo);

    const userManageRolesIsLoading = () => usersLoading && usersLoadingKeys.includes(UsersManagementLoadingKeys.getRoles);
    const userManageRoleUpdateIsLoading = () => usersLoading && usersLoadingKeys.includes(UsersManagementLoadingKeys.updateRoleInfo);
    const userManageRoleDuplicateIsLoading = () => usersLoading && usersLoadingKeys.includes(UsersManagementLoadingKeys.makeDuplicateRole);

    return (
        <>
            <main className="h-full w-full p-4 md:p-8">
                <div className="h-full flex flex-col">
                    <UsersTab 
                        activeTab={tab}
                        access={access}
                        setActiveTab={setTab}
                        auditClick={handleAuditClick}
                        addUserClick={handleAddUserClick}
                        addRoleClick={handleAddRoleClick}
                    />
                    {tab === "users" && (
                        <div className="flex flex-1 flex-col py-4 md:py-6">
                            <div className="flex-1 space-y-4">
                                <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
                                    <div className="flex w-full items-center gap-2 md:contents">
                                        <div className="relative w-full flex-1 md:max-w-sm">
                                            <MagnifyingGlassIcon className="lucide lucide-search absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground"/>
                                            <input
                                                placeholder="Search users..."
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                                className="border-input flex h-11 w-full min-w-0 rounded-lg border bg-white px-3 py-1 pl-10 text-base shadow-xs outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] md:h-9 md:rounded-md md:pl-10 md:text-sm"
                                            />
                                        </div>
                                        {access === "edit" && (
                                            <button
                                                onClick={handleAddUserClick}
                                                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-900 text-white shadow-xs transition-all hover:bg-gray-800 md:hidden"
                                                aria-label="Add User"
                                            >
                                                <PlusIcon className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                    <div className="grid w-full grid-cols-2 gap-3 md:flex md:w-auto">
                                    <div className="min-w-0 md:w-32">
                                        <SelectDropdown
                                            options={[
                                                { label: "All Roles", value: "all_roles" },
                                                ...(userRoles || [])
                                            ]}
                                            value={currentRole}
                                            onChange={setCurrentRole}
                                            disabled={userRolesIsLoading()}
                                            selectClassname="h-11 rounded-lg bg-white py-1.5 md:h-auto md:rounded-md"
                                        />
                                    </div>
                                    <div className="min-w-0 md:w-32">
                                        <SelectDropdown
                                            options={[
                                                { label: "All Status", value: "all_status" },
                                                { label: "Active", value: "ACTIVE" },
                                                { label: "Inactive", value: "DELETED" }
                                            ]}
                                            value={currentStatus}
                                            onChange={setCurrentStatus}
                                            selectClassname="h-11 rounded-lg bg-white py-1.5 md:h-auto md:rounded-md"
                                        />
                                    </div>
                                    </div>
                                </div>
                                <div className="relative hidden overflow-hidden rounded-lg border border-border md:block">
                                    {(usersIsLoading() || userUpdateIsLoading()) && <OverlayGrayBackground />}
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-gray-100">
                                                <tr>
                                                    <th className="w-12 px-4 py-2.5">

                                                    </th>
                                                    <th className="text-left px-4 py-2.5 font-medium text-primary uppercase tracking-wide text-xs">
                                                        Name
                                                    </th>
                                                    <th className="text-left px-4 py-2.5 font-medium text-primary uppercase tracking-wide text-xs">
                                                        Email
                                                    </th>
                                                    <th className="text-left px-4 py-2.5 font-medium text-primary uppercase tracking-wide text-xs">
                                                        Role
                                                    </th>
                                                    <th className="text-left px-4 py-2.5 font-medium text-primary uppercase tracking-wide text-xs">
                                                        Status
                                                    </th>
                                                    {access === "edit" && (
                                                        <th className="text-right px-4 py-2.5 font-medium text-primary uppercase tracking-wide text-xs">
                                                            Actions
                                                        </th>
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(users || []).map((user, index) => {
                                                    return (
                                                        <tr 
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                handleEditClick(user);
                                                            }}
                                                            key={user.id} className={`border-b border-border hover:bg-gray-100 cursor-pointer ${index % 2 ? "bg-gray-50" : "bg-white"}`}
                                                        >
                                                            <td className="p-2">

                                                            </td>
                                                            <td className="p-2">
                                                                <div className="font-medium text-primary">
                                                                    {user.name}
                                                                </div>
                                                            </td>
                                                            <td className="p-2">
                                                                <div className="text-gray-600">
                                                                    {user.email}
                                                                </div>
                                                            </td>
                                                            <td className="p-2">
                                                                <div className={`w-fit px-2 text-xs py-0.5 rounded-md ${getUserRoleColor(user.role)}`}>
                                                                    {user.roleName || ""}
                                                                </div>
                                                            </td>
                                                            <td className="p-2">
                                                                <div className={`w-fit px-2 text-xs py-0.5 rounded-md ${getUserStatusColor(user.status)}`}>
                                                                    {user.status ? enumUserStatuses[user.status] || user.status : ""}
                                                                </div>
                                                            </td>
                                                            {access === "edit" && (
                                                            <td className="p-2 flex justify-end">
                                                                    <UserActionsDropdown
                                                                        user={user}
                                                                        isOpen={openedDropdownUserId === user.id}
                                                                        onClose={() => setOpenedDropdownUserId(null)}
                                                                        onToggle={setOpenedDropdownUserId}
                                                                        onRemoveClick={handleRemoveClick}
                                                                        onDisableClick={handleDisableClick}
                                                                        onEnableClick={handleEnableClick}
                                                                        onEditClick={handleEditClick}
                                                                    />
                                                                </td>
                                                            )}
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="relative -mx-4 md:hidden">
                                    {(usersIsLoading() || userUpdateIsLoading()) && <OverlayGrayBackground />}
                                    {(users || []).map((user) => (
                                        <div
                                            key={user.id}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleEditClick(user);
                                            }}
                                            className="flex cursor-pointer items-start gap-3 border-b border-gray-200 px-4 py-3 transition-colors hover:bg-gray-50"
                                        >
                                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-600">
                                                {getUserInitials(user.name)}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate font-semibold text-gray-900">{user.name}</div>
                                                <div className="truncate text-sm text-gray-500">{user.email}</div>
                                                <div className="mt-2 inline-flex rounded-md bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                                                    {formatDisplayName(user.roleName)}
                                                </div>
                                            </div>
                                            <div className="flex shrink-0 flex-col items-end gap-2">
                                                <div className={`rounded-md px-2.5 py-1 text-xs font-semibold ${user.status === ACTIVE ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600"}`}>
                                                    {user.status ? enumUserStatuses[user.status] || user.status : ""}
                                                </div>
                                                {access === "edit" && (
                                                    <UserActionsDropdown
                                                        user={user}
                                                        isOpen={openedDropdownUserId === user.id}
                                                        onClose={() => setOpenedDropdownUserId(null)}
                                                        onToggle={setOpenedDropdownUserId}
                                                        onRemoveClick={handleRemoveClick}
                                                        onDisableClick={handleDisableClick}
                                                        onEnableClick={handleEnableClick}
                                                        onEditClick={handleEditClick}
                                                        compact
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {!isFirstLoadComplete && (
                                    <div className="z-10 h-20">
                                        <LoaderSpinner width='70' height='70' border='3' colorSpinner='black' />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {tab === "roles" && (
                        <div className="flex flex-1 py-4 md:py-6">
                            <div className="flex-1 space-y-4">
                                <div className="flex items-center gap-4">
                                    <div className="flex w-full items-center gap-2 md:contents">
                                        <div className="relative w-full flex-1 md:max-w-sm">
                                            <MagnifyingGlassIcon className="lucide lucide-search absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground"/>
                                            <input
                                                placeholder="Search roles..."
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                                className="border-input flex h-11 w-full min-w-0 rounded-lg border bg-white px-3 py-1 pl-10 text-base shadow-xs outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] md:h-9 md:rounded-md md:pl-10 md:text-sm"
                                            />
                                        </div>
                                        {access === "edit" && (
                                            <button
                                                onClick={handleAddRoleClick}
                                                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-900 text-white shadow-xs transition-all hover:bg-gray-800 md:hidden"
                                                aria-label="Create Role"
                                            >
                                                <PlusIcon className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="hidden overflow-hidden rounded-lg border border-border md:block">
                                    {(userManageRolesIsLoading() || userManageRoleUpdateIsLoading() || userManageRoleDuplicateIsLoading()) && <OverlayGrayBackground />}
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-gray-100">
                                                <tr>
                                                    <th className="text-left px-4 py-2.5 font-medium text-primary uppercase tracking-wide text-xs">
                                                        Role Name
                                                    </th>
                                                    <th className="text-left px-4 py-2.5 font-medium text-primary uppercase tracking-wide text-xs">
                                                        Users Assigned
                                                    </th>
                                                    <th className="text-left px-4 py-2.5 font-medium text-primary uppercase tracking-wide text-xs">
                                                        Permissions
                                                    </th>
                                                    <th className="text-left px-4 py-2.5 font-medium text-primary uppercase tracking-wide text-xs">
                                                        Created
                                                    </th>
                                                    <th className="text-left px-4 py-2.5 font-medium text-primary uppercase tracking-wide text-xs">
                                                        Updated
                                                    </th>
                                                    {access === "edit" && (
                                                        <th className="text-right px-4 py-2.5 font-medium text-primary uppercase tracking-wide text-xs">
                                                            Actions
                                                        </th>
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(roles || []).map((role, index) => {
                                                    return (
                                                        <tr 
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                handleEditRoleClick(role);
                                                            }}
                                                            key={role.id}
                                                            className={`border-b border-border hover:bg-gray-100 cursor-pointer ${index % 2 ? "bg-gray-50" : "bg-white"}`}
                                                        >
                                                            <td className="p-2 max-w-[200px]">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="font-medium text-primary flex items-center gap-2">
                                                                        <span>{role.name}</span>
                                                                        {role.description && (
                                                                            <InformationCircleIcon
                                                                                className="h-4 w-4 text-gray-400"
                                                                                title={role.description}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="p-2">
                                                                <button 
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        setTab("users");
                                                                        setCurrentRole(role.id);
                                                                    }}
                                                                    className="group inline-flex items-center justify-center whitespace-nowrap text-xs transition-all disabled:pointer-events-none disabled:opacity-50 shrink-0 outline-none hover:bg-gray-200 rounded-md gap-1 h-auto p-0 font-medium text-primary"
                                                                >
                                                                    <UsersIcon className="h-4 w-4 text-gray-600 group-hover:text-gray-900"/>
                                                                    <span className="text-gray-700 group-hover:text-gray-900">{role.usersAssigned || 0}</span>
                                                                </button>
                                                            </td>
                                                            <td className="p-2 max-w-[250px]">
                                                                {(() => {
                                                                    const { editCount, viewCount, total } = countPermissions(role.permissions);

                                                                    return (
                                                                        <div className="flex items-center gap-2 text-xs">
                                                                            <span className="inline-flex min-w-[52px] justify-start whitespace-nowrap tabular-nums bg-primary text-white border border-primary px-1.5 py-0.5 rounded-md">
                                                                                {editCount} Edit
                                                                            </span>
                                                                            <span className="inline-flex min-w-[52px] justify-start whitespace-nowrap tabular-nums bg-gray-500 text-white border border-gray-500 px-1.5 py-0.5 rounded-md">
                                                                                {viewCount} View
                                                                            </span>
                                                                            <span className="whitespace-nowrap text-gray-600">of {total} views</span>
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </td>
                                                            <td className="p-2">
                                                                {(() => {
                                                                    const timestamp = formatRoleTimestamp(role.createdAt);
                                                                    return timestamp ? (
                                                                        <div className="flex flex-col">
                                                                            <span className="text-sm text-gray-700">{timestamp.date}</span>
                                                                            <span className="text-xs text-gray-500">{timestamp.time}</span>
                                                                        </div>
                                                                    ) : null;
                                                                })()}
                                                            </td>
                                                            <td className="p-2">
                                                                {(() => {
                                                                    const timestamp = formatRoleTimestamp(role.updatedAt);
                                                                    return timestamp ? (
                                                                        <div className="flex flex-col">
                                                                            <span className="text-sm text-gray-700">{timestamp.date}</span>
                                                                            <span className="text-xs text-gray-500">{timestamp.time}</span>
                                                                        </div>
                                                                    ) : null;
                                                                })()}
                                                            </td>
                                                            {access === "edit" && (
                                                            <td className="p-2">
                                                                    <RolesActionsDropdown
                                                                        role={role}
                                                                        isOpen={openedDropdownUserId === role.id}
                                                                        onClose={() => setOpenedDropdownUserId(null)}
                                                                        onToggle={setOpenedDropdownUserId}
                                                                        onRemoveClick={handleRemoveRoleClick}
                                                                        onDuplicateClick={handleDuplicateRoleClick}
                                                                        onEditClick={handleEditRoleClick}
                                                                    />
                                                                </td>
                                                            )}
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="relative -mx-4 md:hidden">
                                    {(userManageRolesIsLoading() || userManageRoleUpdateIsLoading() || userManageRoleDuplicateIsLoading()) && <OverlayGrayBackground />}
                                    {(roles || []).map((role) => {
                                        const { editCount, viewCount } = countPermissions(role.permissions);
                                        return (
                                            <div
                                                key={role.id}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleEditRoleClick(role);
                                                }}
                                            className="cursor-pointer border-b border-gray-200 px-4 py-3 transition-colors hover:bg-gray-50"
                                            >
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="min-w-0">
                                                        <div className="truncate font-semibold text-gray-900">{formatDisplayName(role.name)}</div>
                                                        {role.description && (
                                                            <div className="truncate text-sm text-gray-500">{role.description}</div>
                                                        )}
                                                        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                                                            <span className="inline-flex rounded-md border border-gray-900 bg-gray-900 px-2.5 py-1 font-medium text-white md:border-primary md:bg-primary">
                                                                {editCount} Edit
                                                            </span>
                                                            <span className="inline-flex rounded-md border border-slate-600 bg-slate-600 px-2.5 py-1 font-medium text-white">
                                                                {viewCount} View
                                                            </span>
                                                            <span className="text-gray-500">of {totalPermissionTabs} tabs</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex shrink-0 items-center gap-2">
                                                        <button
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                setTab("users");
                                                                setCurrentRole(role.id);
                                                            }}
                                                            className="inline-flex items-center gap-1 text-sm font-medium text-gray-600"
                                                        >
                                                            <UsersIcon className="h-4 w-4" />
                                                            <span>{role.usersAssigned || 0}</span>
                                                        </button>
                                                        {access === "edit" && (
                                                            <RolesActionsDropdown
                                                                role={role}
                                                                isOpen={openedDropdownUserId === role.id}
                                                                onClose={() => setOpenedDropdownUserId(null)}
                                                                onToggle={setOpenedDropdownUserId}
                                                                onRemoveClick={handleRemoveRoleClick}
                                                                onDuplicateClick={handleDuplicateRoleClick}
                                                                onEditClick={handleEditRoleClick}
                                                                compact
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {!isFirstLoadComplete && (
                                    <div className="z-10 h-20">
                                        <LoaderSpinner width='70' height='70' border='3' colorSpinner='black' />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </main>
            <DisableAccessModal
                isOpen={access === "edit" && openedModals.disable.isOpen}
                user={openedModals.disable.user}
                cancelClick={() => {
                    setOpenedModals((prev) => {
                        return {
                            ...prev,
                            disable: {
                                isOpen: false
                            }
                        }
                    });
                }}
                submitClick={(id, reason) => handleDisableSubmitClick(id, reason)}
            />
            <EnableAccessModal
                isOpen={access === "edit" && openedModals.enable.isOpen}
                user={openedModals.enable.user}
                cancelClick={() => {
                    setOpenedModals((prev) => {
                        return {
                            ...prev,
                            enable: {
                                isOpen: false
                            }
                        }
                    });
                }}
                submitClick={(id) => handleEnableSubmitClick(id)}
            />
            <RemoveUserModal
                isOpen={access === "edit" && openedModals.remove.isOpen}
                user={openedModals.remove.user}
                cancelClick={() => {
                    setOpenedModals((prev) => {
                        return {
                            ...prev,
                            remove: {
                                isOpen: false
                            }
                        }
                    });
                }}
                submitClick={(id) => handleRemoveSubmitClick(id)}
            />
            <RemoveRoleModal
                isOpen={access === "edit" && openedModals.removeRole.isOpen}
                role={openedModals.removeRole.role}
                allRoles={rolesToReassign}
                cancelClick={() => {
                    setOpenedModals((prev) => {
                        return {
                            ...prev,
                            removeRole: {
                                isOpen: false
                            }
                        }
                    });
                }}
                submitClick={(id, newRole) => removeRoleSubmitClick(id, newRole)}
            />
            <AddUserModal
                isOpen={access === "edit" && openedModals.create.isOpen}
                userRolesOptions={userRoles}
                users={users}
                cancelClick={() => {
                    setOpenedModals((prev) => {
                        return {
                            ...prev,
                            create: {
                                isOpen: false
                            }
                        }
                    });
                }}
                submitClick={handleAddUser}
            />
            <ConfrimModal
                isOpen={access === "edit" && openedModals?.duplicateRole?.isOpen}
                title={`Are you sure want to duplicate this role ${openedModals?.duplicateRole?.role?.name || ""}?`}
                confirmButtonTitle={"Duplicate"}
                containerClassName={"max-w-[35%]"}
                isLoading={userManageRoleDuplicateIsLoading()}
                submitClick={async () => {
                    await handleDuplicateSubmitClick();
                }}
                cancelClick={() => {
                    setOpenedModals((prev) => {
                        return {
                            ...prev,
                            duplicateRole: {
                                isOpen: false
                            }
                        }
                    });
                }}
            />
            <AuditDrawer
                isOpen={sidebar?.audit?.isOpen}
                onClose={() => setSidebar({
                    user: {
                        isOpen: false,
                    },
                    role: {
                        isOpen: false
                    },
                    roleCreate: {
                        isOpen: false
                    },
                    audit: {
                        isOpen: false
                    }
                })}
            />
            <UserDrawer
                optionsByRoles={userRoles}
                isOpen={access === "edit" && sidebar?.user?.isOpen}
                isLoading={userUpdateIsLoading()}
                user={sidebar?.user?.user}
                onClose={() => setSidebar({
                    user: {
                        isOpen: false,
                    },
                    role: {
                        isOpen: false
                    },
                    roleCreate: {
                        isOpen: false
                    },
                    audit: {
                        isOpen: false
                    }
                })}
                onSuccessSubmit={handleUpdateUser}
            />
            <RoleDrawer
                type={sidebar?.roleCreate?.isOpen ? "create" : "edit"}
                isOpen={access === "edit" && (sidebar?.role?.isOpen || sidebar?.roleCreate?.isOpen)}
                isLoading={userManageRoleUpdateIsLoading()}
                role={sidebar?.role?.role || {}}
                onClose={() => setSidebar({
                    user: {
                        isOpen: false,
                    },
                    role: {
                        isOpen: false
                    },
                    roleCreate: {
                        isOpen: false
                    },
                    audit: {
                        isOpen: false
                    }
                })}
                onSuccessSubmit={handleUpdateRole}
            />
        </>
    )
}